import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

export default function CompareModelsGraphModal({ isOpen, onClose, compareResult }) {
  if (!isOpen || !compareResult) return null;

  // Extract model names, excluding the 'comparison' key
  const modelNames = Object.keys(compareResult).filter(key => key !== 'comparison');

  // Create structured data for Recharts
  const latencyData = modelNames.map(model => ({
    name: model,
    latency: compareResult[model]?.analytics.latency || 0,
    provider: getProvider(model)
  }));

  const costData = modelNames.map(model => ({
    name: model,
    cost: compareResult[model]?.analytics.cost || 0,
    provider: getProvider(model)
  }));

  const tokenData = modelNames.map(model => ({
    name: model,
    inputTokens: compareResult[model]?.analytics.input_tokens || 0,
    outputTokens: compareResult[model]?.analytics.output_tokens || 0,
    totalTokens: (compareResult[model]?.analytics.input_tokens || 0) +
      (compareResult[model]?.analytics.output_tokens || 0),
    provider: getProvider(model)
  }));

  // Helper function to determine provider
  function getProvider(modelName) {
    if (modelName.includes('gpt')) return 'openai';
    if (modelName.includes('claude')) return 'anthropic';
    if (modelName.includes('gemini')) return 'google';
    return 'other';
  }

  // Get color based on provider
  function getBarColor(provider) {
    switch (provider) {
      case 'openai': return '#10B981'; // green
      case 'anthropic': return '#8B5CF6'; // purple
      case 'google': return '#3B82F6'; // blue
      default: return '#9CA3AF'; // gray
    }
  }

  // Define consistent colors for token metrics
  const tokenColors = {
    inputTokens: '#3B82F6',  // blue
    outputTokens: '#8B5CF6', // purple
    totalTokens: '#10B981'   // green
  };

  // Format number for tooltip
  const formatNumber = (value, metric) => {
    if (metric === 'cost') {
      return `$${value.toFixed(6)}`;
    }
    if (metric === 'latency') {
      return `${value.toFixed(2)}s`;
    }
    return value.toLocaleString();
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label, metric }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium text-gray-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatNumber(entry.value, metric)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">Model Comparison Visualization</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-4 flex-1 overflow-auto space-y-8">
          {/* Summary of what we're comparing */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-lg text-ktx-purple mb-2">Comparison Overview</h4>
            <p className="text-sm text-gray-600">
              Visualizing performance metrics across {modelNames.length} different models. Charts show latency, cost, and token usage for each model.
            </p>

            {/* Legend for model providers */}
            <div className="mt-3 flex flex-wrap gap-4">
              <div className="flex items-center">
                <div className="w-4 h-4 mr-2" style={{ backgroundColor: getBarColor('openai') }}></div>
                <span className="text-xs">OpenAI</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 mr-2" style={{ backgroundColor: getBarColor('anthropic') }}></div>
                <span className="text-xs">Anthropic</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 mr-2" style={{ backgroundColor: getBarColor('google') }}></div>
                <span className="text-xs">Google</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 mr-2" style={{ backgroundColor: getBarColor('other') }}></div>
                <span className="text-xs">Other</span>
              </div>
            </div>
          </div>

          {/* Latency Chart */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-lg text-gray-800 mb-4">Latency Comparison</h4>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={latencyData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Seconds', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip metric="latency" />} />
                  <Bar
                    dataKey="latency"
                    name="Latency"
                    fillOpacity={0.8}
                    strokeWidth={1}
                    isAnimationActive={true}
                    animationDuration={500}
                  >
                    {latencyData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={getBarColor(entry.provider)} 
                        stroke={getBarColor(entry.provider)} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Lower values indicate faster response times. Measured in seconds.
            </p>
          </div>

          {/* Cost Chart */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-lg text-gray-800 mb-4">Cost Comparison</h4>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={costData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'USD', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip metric="cost" />} />
                  <Bar
                    dataKey="cost"
                    name="Cost"
                    fillOpacity={0.8}
                    strokeWidth={1}
                    isAnimationActive={true}
                    animationDuration={500}
                  >
                    {costData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={getBarColor(entry.provider)} 
                        stroke={getBarColor(entry.provider)} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Lower values indicate more cost-effective models. Measured in USD.
            </p>
          </div>

          {/* Tokens Chart */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-lg text-gray-800 mb-4">Token Usage Comparison</h4>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={tokenData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="inputTokens"
                    name="Input Tokens"
                    fill={tokenColors.inputTokens}
                    fillOpacity={0.8}
                    strokeWidth={1}
                  />
                  <Bar
                    dataKey="outputTokens"
                    name="Output Tokens"
                    fill={tokenColors.outputTokens}
                    fillOpacity={0.8}
                    strokeWidth={1}
                  />
                  <Bar
                    dataKey="totalTokens"
                    name="Total Tokens"
                    fill={tokenColors.totalTokens}
                    fillOpacity={0.8}
                    strokeWidth={1}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Shows input, output, and total token usage for each model.
            </p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-ktx-purple hover:bg-ktx-purple-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}